/**
 * 向导内 YAML 生成 / 保存失败的统一通知（复用模型中心 `showYamlTestFailureFeedback`）。
 */
import { useCallback } from 'react';
import { useIntl } from 'umi';

import {
  showYamlTestFailureFeedback,
  type TShowYamlTestFailureInput,
  type TYamlTestFormatMessage,
} from '@/pages/datalab/pages/Number/ModelManagementCenter/utils/showYamlTestFailureFeedback';

interface IUseWizardYamlFeedbackResult {
  showSaveDraftFailure: (input: TShowYamlTestFailureInput) => void;
  showGenerateYamlFailure: (input: TShowYamlTestFailureInput) => void;
}

/**
 * 按当前 Cube 展示名注入失败标题；文案 id 区分生成与保存场景。
 */
export const useWizardYamlFeedback = (
  cubeNameSummary: string,
): IUseWizardYamlFeedbackResult => {
  const { formatMessage } = useIntl();
  const formatYamlFeedback =
    formatMessage as unknown as TYamlTestFormatMessage;

  const showSaveDraftFailure = useCallback(
    (input: TShowYamlTestFailureInput): void => {
      showYamlTestFailureFeedback(formatYamlFeedback, input, {
        title: {
          id: 'dbl_model_management_create_model_save_failed',
          values: { name: cubeNameSummary },
        },
        fallbackMsg: {
          id: 'dbl_model_management_create_model_save_failed_hint',
        },
        detailTitle: { id: 'dbl_model_management_yaml_save_detail_title' },
        batchLongSummary: {
          id: 'dbl_model_management_yaml_save_batch_long_summary',
        },
      });
    },
    [cubeNameSummary, formatYamlFeedback],
  );

  const showGenerateYamlFailure = useCallback(
    (input: TShowYamlTestFailureInput): void => {
      showYamlTestFailureFeedback(formatYamlFeedback, input, {
        title: {
          id: 'dbl_model_management_create_model_generate_failed',
          values: { name: cubeNameSummary },
        },
        fallbackMsg: {
          id: 'dbl_model_management_create_model_generate_failed_hint',
        },
        detailTitle: {
          id: 'dbl_model_management_yaml_generate_detail_title',
        },
      });
    },
    [cubeNameSummary, formatYamlFeedback],
  );

  return { showSaveDraftFailure, showGenerateYamlFailure };
};
